import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Loader, CheckCircle, AlertCircle } from "lucide-react";
import { useUserStore } from "../stores/useUserStore";

const ForgotPasswordPage = () => {
	const [email, setEmail] = useState("");
	const [isSubmitted, setIsSubmitted] = useState(false);

	const { forgotPassword, loading } = useUserStore();

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!email.trim()) {
			return;
		}

		const result = await forgotPassword(email.trim());
		
		if (result.success) {
			setIsSubmitted(true);
		}
	};

	// אם המייל נשלח - הצג הודעת אישור
	if (isSubmitted) {
		return (
			<div className='flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-white'>
				<motion.div
					className='sm:mx-auto sm:w-full sm:max-w-md'
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.8 }}
				>
					<div className='bg-white border border-gray-200 py-8 px-4 shadow sm:rounded-lg sm:px-10'>
						<div className='text-center'>
							<motion.div
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								transition={{ duration: 0.5, delay: 0.2 }}
								className='mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6' style={{backgroundColor: 'rgba(100, 13, 70, 0.1)'}}
							>
								<CheckCircle className='h-8 w-8 text-brand-primary' />
							</motion.div>
							
							<h2 className='text-2xl font-extrabold text-brand-primary mb-4'>
								המייל נשלח! 📧
							</h2>
							
							<div className='rounded-lg p-4 mb-6' style={{backgroundColor: 'rgba(100, 13, 70, 0.05)', borderColor: 'rgba(100, 13, 70, 0.2)', color: 'rgba(100, 13, 70, 0.8)'}}>
								<p className='text-emerald-800 text-sm leading-relaxed'>
									נשלח מייל עם קישור לאיפוס סיסמה לכתובת:
									<br />
									<strong className='font-medium'>{email}</strong>
								</p>
							</div>
							
							<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6'>
								<div className='flex items-start'>
									<AlertCircle className='h-5 w-5 text-yellow-600 mt-0.5 ml-2' />
									<div className='text-yellow-800 text-sm text-right'>
										<p className='font-medium mb-1'>חשוב לדעת:</p>
										<ul className='list-disc list-inside space-y-1 text-xs'>
											<li>הקישור תקף ל-15 דקות בלבד</li>
											<li>בדוק גם בתיקיית ספאם</li>
											<li>אם לא הגיע מייל, נסה שוב</li>
										</ul>
									</div>
								</div>
							</div>
							
							<div className='space-y-3'>
								<button
									onClick={() => {
										setIsSubmitted(false);
										setEmail("");
									}}
									className='w-full flex justify-center py-2 px-4 border border-gray-300 
									rounded-md shadow-sm text-sm font-medium text-gray-700 bg-gray-50
									hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2
									focus:ring-brand-primary transition duration-150 ease-in-out'
								>
									שלח מייל שוב
								</button>
								
								<Link
									to='/login'
									className='w-full flex justify-center py-2 px-4 border border-transparent 
									rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary
									hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2
									focus:ring-brand-primary transition duration-150 ease-in-out'
								>
									חזור להתחברות
								</Link>
							</div>
						</div>
					</div>
				</motion.div>
			</div>
		);
	}

	// טופס שליחת בקשת איפוס
	return (
		<div className='flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-white'>
			<motion.div
				className='sm:mx-auto sm:w-full sm:max-w-md'
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
			>
				<h2 className='mt-6 text-center text-3xl font-extrabold text-brand-primary'>
					שכחת סיסמה? 🔐
				</h2>
				<p className='mt-2 text-center text-sm text-gray-600'>
					אין בעיה! נשלח לך קישור לאיפוס
				</p>
			</motion.div>

			<motion.div
				className='mt-8 sm:mx-auto sm:w-full sm:max-w-md'
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, delay: 0.2 }}
			>
				<div className='bg-white border border-gray-200 py-8 px-4 shadow sm:rounded-lg sm:px-10'>
					<form onSubmit={handleSubmit} className='space-y-6'>
						<div>
							<label htmlFor='email' className='block text-sm font-medium text-gray-700'>
								כתובת אימייל
							</label>
							<div className='mt-1 relative rounded-md shadow-sm'>
								<div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
									<Mail className='h-5 w-5 text-gray-600' aria-hidden='true' />
								</div>
								<input
									id='email'
									type='email'
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									className='block w-full px-3 py-2 pl-10 bg-gray-50 border border-gray-300 
									rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-primary 
									focus:border-brand-primary sm:text-sm'
									placeholder='הכנס את כתובת האימייל שלך'
									disabled={loading}
								/>
							</div>
						</div>

						<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
							<div className='flex items-start'>
								<Mail className='h-5 w-5 text-blue-600 mt-0.5 ml-2' />
								<div className='text-blue-800 text-sm text-right'>
									<p className='font-medium mb-1'>איך זה עובד?</p>
									<p className='text-xs leading-relaxed'>
										נשלח לך מייל עם קישור מאובטח לאיפוס הסיסמה. 
										הקישור יהיה תקף ל-15 דקות בלבד.
									</p>
								</div>
							</div>
						</div>

						<button
							type='submit'
							className='w-full flex justify-center py-2 px-4 border border-transparent 
							rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary
							hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2
							focus:ring-brand-primary transition duration-150 ease-in-out disabled:opacity-50
							disabled:cursor-not-allowed'
							disabled={loading || !email.trim()}
						>
							{loading ? (
								<>
									<Loader className='mr-2 h-5 w-5 animate-spin' aria-hidden='true' />
									שולח מייל...
								</>
							) : (
								<>
									<Mail className='mr-2 h-5 w-5' aria-hidden='true' />
									שלח קישור לאיפוס
								</>
							)}
						</button>
					</form>

					<div className='mt-6'>
						<div className='relative'>
							<div className='absolute inset-0 flex items-center'>
								<div className='w-full border-t border-gray-300' />
							</div>
							<div className='relative flex justify-center text-sm'>
								<span className='px-2 bg-white border border-gray-200 text-gray-600'>או</span>
							</div>
						</div>

						<div className='mt-6'>
							<Link
								to='/login'
								className='w-full flex justify-center items-center py-2 px-4 border border-gray-300 
								rounded-md shadow-sm text-sm font-medium text-gray-700 bg-gray-50
								hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2
								focus:ring-brand-primary transition duration-150 ease-in-out'
							>
								<ArrowLeft className='mr-2 h-4 w-4' />
								חזור להתחברות
							</Link>
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	);
};

export default ForgotPasswordPage; 